/**
 * The attention inbox (IA §2 Home): things waiting for this person, merged from several
 * modules — assistant proposals, tasks and approvals, data-quality findings, failed automations.
 * A module registers a source in its `setup()`; Home lists every source, the top bar's bell the
 * ones marked `bell`. A source shows only when the user holds its permission and its server
 * module is enabled, and lists only items the user may act on.
 *
 *   registerInboxSource({ key: 'tasks', label: msg('Tasks and approvals'), order: 10, bell: true,
 *     permission: 'flows.task.work', module: 'automation', href: '/automation/tasks',
 *     query: () => ({ queryKey: automationKeys.tasks({ status: 'open' }), queryFn: … }),
 *     items: (tasks) => tasks.map((x) => ({ id: `task:${x.id}`, … })) });
 *
 * Use the owner module's query keys, so its mutations and realtime invalidations refresh the
 * inbox too (never share a key with an infinite query: the cached data has another shape).
 */
import { keepPreviousData, type QueryKey, useQueries } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { Me } from './api/types';
import { can, type PermissionCheck } from './can';
import { moduleEnabled, useMe } from './me';

export type InboxTone = 'good' | 'medium' | 'bad' | 'info' | 'neutral' | 'accent' | 'caution';

export interface InboxItem {
  /** Unique across sources, e.g. `task:<id>`. */
  id: string;
  source: string;
  /** What it is (English, marked with msg()): "Approval", "Proposal", "Data quality" … */
  kind: string;
  tone: InboxTone;
  title: string;
  /** One or two lines of context (a task's body, a proposal's rationale, an error). */
  detail?: string | null;
  /** What it is about: rendered as a link to the entity's page. */
  subject?: { type: string; id: string; label?: string | null } | null;
  /** When it arrived (ISO). */
  at?: string | null;
  /** When it is due (ISO); past due is shown as overdue. */
  due?: string | null;
  /** Extra facts, already translated ("failed 3 times in 24 h"). */
  meta?: string[];
  /** Its own page (relative app path). */
  href?: string;
  /** Buttons that act on it right here (compact). */
  actions?: ReactNode;
  /** Lower comes first: 0 overdue or urgent, 1 approvals, 2 tasks, 3 proposals, 4 findings, 5 failures. */
  rank: number;
}

export interface InboxSource<T = unknown> {
  key: string;
  /** English, marked with msg(): the filter chip and the "see all" link. */
  label: string;
  order: number;
  permission?: PermissionCheck;
  /** Server module that must be enabled for the company. */
  module?: string;
  /** Another fact about the account the source needs (e.g. a second permission to read the list). */
  when?: (me: Me) => boolean;
  /** Counted in the top bar's bell (things assigned to the person), not only on Home. */
  bell?: boolean;
  /** Listed in Home's attention card (default). False for a source Home shows in a card of its own (news). */
  home?: boolean;
  /** The full list in its module. */
  href?: string;
  query: () => { queryKey: QueryKey; queryFn: () => Promise<T>; refetchInterval?: number };
  items: (data: T) => InboxItem[];
}

const sources: InboxSource[] = [];

export function registerInboxSource<T>(source: InboxSource<T>): void {
  if (sources.some((s) => s.key === source.key)) return;
  sources.push(source as InboxSource);
  sources.sort((a, b) => a.order - b.order);
}

export interface InboxPart {
  key: string;
  label: string;
  href?: string;
  items: InboxItem[];
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

/** Oldest due first, then newest first; urgent kinds before the rest. */
export function compareInbox(a: InboxItem, b: InboxItem): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  if (a.due && b.due) return a.due.localeCompare(b.due);
  if (a.due !== b.due) return a.due ? -1 : 1;
  return (b.at ?? '').localeCompare(a.at ?? '');
}

/** The sources this user sees, their items merged and sorted (see `compareInbox`). */
export function useInbox({ bell = false }: { bell?: boolean } = {}) {
  const me = useMe();
  const active = sources.filter((s) => (bell ? s.bell : s.home !== false) && can(me, s.permission) && moduleEnabled(me, s.module) && (!s.when || s.when(me)));
  const results = useQueries({ queries: active.map((s) => ({ ...s.query(), placeholderData: keepPreviousData })) });
  const parts: InboxPart[] = active.map((s, i) => {
    const r = results[i];
    return {
      key: s.key,
      label: s.label,
      href: s.href,
      items: r?.data !== undefined ? s.items(r.data) : [],
      isPending: !!r?.isPending,
      error: r?.error ?? null,
      refetch: () => void r?.refetch(),
    };
  });
  const items = parts.flatMap((p) => p.items).sort(compareInbox);
  return {
    items,
    parts,
    /** Nothing loaded yet from any source. */
    isPending: parts.length > 0 && parts.every((p) => p.isPending),
    errors: parts.filter((p) => p.error),
  };
}
